// calendarHelper.js
// Unified helper to create calendar events for both Google and Outlook users
// Import this in AvailableSignups.js and use instead of direct calendarService calls

import { auth, calendarService, outlookCalendarService } from '../firebase/config';

/**
 * Determines which calendar provider the user is using based on their sign-in method
 * @param {Object} currentUser - The current user object from Firebase Auth
 * @returns {string|null} - 'google', 'microsoft', or null
 */
export function getUserCalendarProvider(currentUser) {
  if (!currentUser) {
    console.log('📅 getUserCalendarProvider: No currentUser provided');
    return null;
  }
  
  // Method 1: Check the providers array (set by setupAuthStateListener)
  const providers = currentUser.providers || [];
  console.log('📅 getUserCalendarProvider: Checking providers array:', providers);
  
  if (providers.includes('google.com')) {
    console.log('📅 getUserCalendarProvider: Found google.com in providers array');
    return 'google';
  }
  
  if (providers.includes('microsoft.com')) {
    console.log('📅 getUserCalendarProvider: Found microsoft.com in providers array');
    return 'microsoft';
  }
  
  // Method 2: Check providerData on currentUser object
  const providerData = currentUser.providerData || [];
  console.log('📅 getUserCalendarProvider: Checking currentUser.providerData:', providerData);
  
  for (const provider of providerData) {
    if (provider.providerId === 'google.com') {
      console.log('📅 getUserCalendarProvider: Found google.com in currentUser.providerData');
      return 'google';
    }
    if (provider.providerId === 'microsoft.com') {
      console.log('📅 getUserCalendarProvider: Found microsoft.com in currentUser.providerData');
      return 'microsoft';
    }
  }
  
  // Method 3: Check signInProvider if set
  if (currentUser.signInProvider === 'google.com') {
    console.log('📅 getUserCalendarProvider: Found google.com in signInProvider');
    return 'google';
  }
  if (currentUser.signInProvider === 'microsoft.com') {
    console.log('📅 getUserCalendarProvider: Found microsoft.com in signInProvider');
    return 'microsoft';
  }
  
  // Method 4: FALLBACK - Check auth.currentUser.providerData directly from Firebase Auth
  // This works even if the app's user object doesn't include providerData
  if (auth.currentUser && auth.currentUser.providerData) {
    console.log('📅 getUserCalendarProvider: Checking auth.currentUser.providerData:', auth.currentUser.providerData);
    
    for (const provider of auth.currentUser.providerData) {
      if (provider.providerId === 'google.com') {
        console.log('📅 getUserCalendarProvider: Found google.com in auth.currentUser.providerData');
        return 'google';
      }
      if (provider.providerId === 'microsoft.com') {
        console.log('📅 getUserCalendarProvider: Found microsoft.com in auth.currentUser.providerData');
        return 'microsoft';
      }
    }
  }
  
  console.log('📅 getUserCalendarProvider: No calendar provider found');
  return null;
}

/**
 * Gets the appropriate calendar service for the current user
 * @param {Object} currentUser - The current user object
 * @returns {Object|null} - The calendar service instance or null
 */
export function getCalendarService(currentUser) {
  const provider = getUserCalendarProvider(currentUser);
  
  switch (provider) {
    case 'google':
      return calendarService;
    case 'microsoft':
      return outlookCalendarService;
    default:
      return null;
  }
}

/**
 * Check if the user has calendar permissions
 * @param {Object} currentUser - The current user object
 * @returns {Promise<boolean>}
 */
export async function hasCalendarPermissions(currentUser) {
  const service = getCalendarService(currentUser);
  
  if (!service) {
    console.log('📅 No calendar service available for user');
    return false;
  }
  
  try {
    return await service.hasCalendarPermissions();
  } catch (error) {
    console.error('📅 Error checking calendar permissions:', error);
    return false;
  }
}

/**
 * Creates a substitute job calendar event using the appropriate service
 * This is the main function to use in AvailableSignups.js
 * 
 * @param {Object} currentUser - The current user object
 * @param {Object} jobData - The job/request data
 * @param {Object} substituteInfo - Info about the substitute
 * @returns {Promise<Object|null>} - The created event or null
 */
export async function createSubstituteJobCalendarEvent(currentUser, jobData, substituteInfo) {
  const provider = getUserCalendarProvider(currentUser);
  const service = getCalendarService(currentUser);
  
  if (!service) {
    console.log('📅 No calendar service available - skipping event creation');
    return null;
  }
  
  console.log(`📅 Creating calendar event via ${provider}...`);
  
  try {
    // Check permissions first
    const hasPerms = await service.hasCalendarPermissions();
    if (!hasPerms) {
      console.log(`📅 No ${provider} calendar permissions - skipping event creation`);
      return null;
    }
    
    // Create the event
    const result = await service.createSubstituteJobEvent(jobData, substituteInfo);
    console.log(`✅ Calendar event created successfully via ${provider}:`, result?.id || result);
    return result;
  } catch (error) {
    console.error(`❌ Failed to create calendar event via ${provider}:`, error);
    // Don't throw - calendar events are optional, shouldn't break the main flow
    return null;
  }
}

/**
 * Creates a teacher absence calendar event
 * @param {Object} currentUser - The current user object
 * @param {Object} requestData - The absence request data
 * @returns {Promise<Object|null>}
 */
export async function createTeacherAbsenceCalendarEvent(currentUser, requestData) {
  const provider = getUserCalendarProvider(currentUser);
  const service = getCalendarService(currentUser);
  
  if (!service) {
    console.log('📅 No calendar service available - skipping event creation');
    return null;
  }
  
  console.log(`📅 Creating absence event via ${provider}...`);
  
  try {
    const hasPerms = await service.hasCalendarPermissions();
    if (!hasPerms) {
      console.log(`📅 No ${provider} calendar permissions - skipping event creation`);
      return null;
    }
    
    const result = await service.createTeacherAbsenceEvent(requestData);
    console.log(`✅ Absence event created successfully via ${provider}`);
    return result;
  } catch (error) {
    console.error(`❌ Failed to create absence event via ${provider}:`, error);
    return null;
  }
}

/**
 * Updates a calendar event
 * @param {Object} currentUser - The current user object
 * @param {string} eventId - The event ID to update
 * @param {Object} updatedData - The updated event data
 * @returns {Promise<Object|null>}
 */
export async function updateCalendarEvent(currentUser, eventId, updatedData) {
  const service = getCalendarService(currentUser);
  
  if (!service || !eventId) {
    return null;
  }
  
  try {
    return await service.updateCalendarEvent(eventId, updatedData);
  } catch (error) {
    console.error('❌ Failed to update calendar event:', error);
    return null;
  }
}

/**
 * Deletes a calendar event
 * @param {Object} currentUser - The current user object
 * @param {string} eventId - The event ID to delete
 * @returns {Promise<boolean>}
 */
export async function deleteCalendarEvent(currentUser, eventId) {
  const service = getCalendarService(currentUser);
  
  if (!service || !eventId) {
    return false;
  }
  
  try {
    await service.deleteCalendarEvent(eventId);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete calendar event:', error);
    return false;
  }
}

/**
 * Gets the display name for the user's calendar provider
 * @param {Object} currentUser - The current user object
 * @returns {string}
 */
export function getCalendarProviderDisplayName(currentUser) {
  const provider = getUserCalendarProvider(currentUser);
  
  switch (provider) {
    case 'google':
      return 'Google Calendar';
    case 'microsoft':
      return 'Outlook Calendar';
    default:
      return 'No Calendar';
  }
}

// Default export for convenience
const calendarHelper = {
  getUserCalendarProvider,
  getCalendarService,
  hasCalendarPermissions,
  createSubstituteJobCalendarEvent,
  createTeacherAbsenceCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getCalendarProviderDisplayName
};

export default calendarHelper;